import { useUpdateMediaLibFileMutation } from "@api/media-library";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { type KeyboardEventHandler, useState } from "react";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { track } from "@/features/analytics";
import type { IMediaFile } from "@/shared/types";
import { MediaLibDialogBase } from "../../media-lib-dialog-base";

const DIALOG_ID = "media-lib-rename-file";

interface IProps {
  fileData: IMediaFile;
  onUpdateFile: (fileId: string, fileData: IMediaFile) => void;
}

export const MediaLibFileRenameDialog = ({
  fileData,
  onUpdateFile,
}: IProps) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState(fileData.title);

  const [updateFile, { isLoading }] = useUpdateMediaLibFileMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (!name?.trim() || isLoading) return;

    try {
      const updatedFileData = await updateFile({
        id: fileData._id,
        data: {
          title: name.trim(),
        },
      }).unwrap();
      onUpdateFile(updatedFileData._id, updatedFileData);
    } catch (e) {
      track("error updating media file name", {
        fileData,
        newfileName: name.trim(),
        error: e,
      });
      toast.error("Error updating file name");
    }

    handleClose();
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <MediaLibDialogBase>
      <MediaLibDialogBase.Header title="Rename file" onClose={handleClose} />
      <MediaLibDialogBase.Content>
        <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px] tw-font-semibold">
          Name
        </span>
        <Input
          placeholder="Add your file name here..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </MediaLibDialogBase.Content>
      <MediaLibDialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!name?.trim()}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </MediaLibDialogBase.Footer>
    </MediaLibDialogBase>
  );
};

MediaLibFileRenameDialog.dialogId = DIALOG_ID;
